import React, { useRef, useState } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
const AnimatedText = ({
  text,
  delay = 0
}: {
  text: string;
  delay?: number;
}) => {
  return <span className="inline-block" data-id="element-132">
      {text.split("").map((letter, i) => <span key={i} className="hero-letter" style={{
      animationDelay: `${delay + i * 0.02}s`
    }} data-id="element-133">
          {letter === " " ? "\u00A0" : letter}
        </span>)}
    </span>;
};
const ArrowSlideButton = ({
  children,
  onClick
}: {
  children: React.ReactNode;
  onClick: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  return <motion.button className="relative group px-8 py-4 bg-primary hover:bg-sage-700 text-primary-foreground rounded-lg font-medium transition-colors duration-300 flex items-center" onClick={onClick} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} animate={{
    boxShadow: ["0 0 0 0 rgba(94, 130, 98, 0)", "0 0 0 4px rgba(94, 130, 98, 0.1)", "0 0 0 0 rgba(94, 130, 98, 0)"]
  }} transition={{
    boxShadow: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }} data-id="element-134">
      <motion.span className="relative z-10" animate={{
      x: isHovered ? -4 : 0
    }} transition={{
      duration: 0.3,
      ease: "easeInOut"
    }} data-id="element-135">
        {children}
      </motion.span>
      
      <motion.div className="overflow-hidden flex items-center justify-center" animate={{
      width: isHovered ? 20 : 0,
      opacity: isHovered ? 1 : 0,
      marginLeft: isHovered ? 8 : 0
    }} transition={{
      duration: 0.3,
      ease: "easeInOut"
    }} data-id="element-136">
        <ArrowRight className="w-4 h-4 flex-shrink-0" data-id="element-137" />
      </motion.div>
    </motion.button>;
};
export function Hero() {
  const containerRef = useRef<HTMLElement>(null);
  const scrollY = useMotionValue(0);
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  React.useEffect(() => {
    const updateScrollY = () => scrollY.set(window.scrollY);
    window.addEventListener('scroll', updateScrollY);
    return () => window.removeEventListener('scroll', updateScrollY);
  }, [scrollY]);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  return <div className="relative w-full min-h-screen" onMouseMove={handleMouseMove} data-id="element-138">
      {/* Dot pattern layers */}
      <motion.div className="absolute inset-0 w-full h-full z-[5]" style={{
      y: useTransform(scrollY, [0, 500], [0, -30])
    }} data-id="element-139">
        {/* Base layer - desktop */}
        <div className="absolute inset-0 w-full h-full hidden md:block" style={{
        backgroundImage: 'radial-gradient(circle, rgba(94, 130, 98, 0.15) 1px, transparent 0)',
        backgroundSize: '32px 32px',
        backgroundPosition: '0 0'
      }} data-id="element-140" />
        
        {/* Base layer - mobile */}
        <div className="absolute inset-0 w-full h-full md:hidden" style={{
        backgroundImage: 'radial-gradient(circle, rgba(94, 130, 98, 0.30) 1px, transparent 0)',
        backgroundSize: '32px 32px',
        backgroundPosition: '0 0'
      }} data-id="element-141" />
        
        
        {/* Bright layer - only visible near cursor through mask */}
        <div className="absolute inset-0 w-full h-full pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle, rgba(94, 130, 98, 0.40) 1px, transparent 0)',
        backgroundSize: '32px 32px',
        backgroundPosition: '0 0',
        maskImage: `radial-gradient(circle 150px at ${mousePos.x}px ${mousePos.y}px, 
                        black, transparent)`,
        WebkitMaskImage: `radial-gradient(circle 150px at ${mousePos.x}px ${mousePos.y}px, 
                              black, transparent)`
      }} data-id="element-142" />
      </motion.div>

      <section ref={containerRef} id="hero" className="relative container max-w-7xl mx-auto min-h-[90vh] flex items-center py-16 sm:py-28 mt-12 sm:mt-16 overflow-hidden z-10" data-id="element-143">
      
      {/* Background Effects */}
      <motion.div className="absolute inset-0 z-1" style={{
        y: useTransform(scrollY, [0, 500], [0, -40]),
        x: useTransform(scrollY, [0, 500], [0, -10])
      }} data-id="element-144">
        {/* Simple radial gradient */}
        <div className="absolute inset-0 bg-gradient-radial from-sage-600/8 via-transparent to-transparent" data-id="element-145" />
        
        {/* Background orb with independent parallax */}
        <motion.div className="absolute top-1/3 right-1/4 w-96 h-96 bg-gradient-radial from-sage-600/12 to-transparent rounded-full blur-3xl" style={{
          y: useTransform(scrollY, [0, 500], [0, -25]),
          x: useTransform(scrollY, [0, 500], [0, 15])
        }} data-id="element-146" />
      </motion.div>
      
      

      <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-8 md:gap-16 items-center relative z-10" data-id="element-147">
        <div className="text-left order-2 md:order-1 md:pr-4" data-id="element-148">
          <motion.h1 className="font-display font-semibold text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[96px] leading-[0.95] text-slate-900 mb-4 sm:mb-6 tracking-tight" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5
          }} data-id="element-149">
            <AnimatedText text="RealtyZen" delay={0.2} data-id="element-150" />
            <motion.span initial={{
              width: 0,
              opacity: 0
            }} animate={{
              width: "auto",
              opacity: 1
            }} transition={{
              delay: 1.0,
              duration: 0.6,
              ease: "easeOut"
            }} className="inline-block mx-4 text-sage-600" data-id="element-151">
              —
            </motion.span>
            <motion.span initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.3,
              duration: 0.8,
              ease: [0.25, 0.46, 0.45, 0.94]
            }} className="block mt-3 md:mt-0 md:inline text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-slate-700 font-normal tracking-normal" data-id="element-152">
              your AI front desk.
            </motion.span>
          </motion.h1>
          
          <motion.p className="text-lg sm:text-xl text-foreground/80 mt-6 mb-8 sm:mb-12 leading-relaxed max-w-xl" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.8,
            duration: 0.8,
            ease: "easeOut"
          }} data-id="element-153">
            Handle every inquiry, task & document while you're out showing homes.
          </motion.p>
          
          <motion.div className="flex justify-start" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 2.2,
            duration: 0.8,
            ease: "easeOut"
          }} data-id="element-154">
            <ArrowSlideButton onClick={() => window.location.href = '/waitlist'} data-id="element-155">
              Get Free Early Access
            </ArrowSlideButton>
          </motion.div>
        </div>
        
        <motion.div className="order-1 md:order-2 flex justify-center md:justify-end items-center" style={{
          y: useTransform(scrollY, [0, 500], [0, -60]),
          x: useTransform(scrollY, [0, 500], [0, 8])
        }} data-id="element-156">
          <motion.div className="w-[240px] sm:w-[320px] md:w-[380px] lg:w-[420px] xl:w-[460px] relative aspect-square" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 1.0,
            duration: 1.2,
            ease: "easeOut"
          }} data-id="element-157">
            <motion.img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/467f4cdb-2894-41d0-b9d2-9b626cee04d5/2ab36819-d3d4-4113-a138-ea6c9809229a/Career_3.svg" alt="RealtyZen Illustration" className="w-full h-full object-contain relative z-10 drop-shadow-2xl" loading="eager" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 1.5,
              duration: 1.5,
              ease: "easeOut"
            }} data-id="element-158" />
            
            {/* Static background orb with subtle glow */}
            <div className="absolute inset-0 -z-10 bg-gradient-radial from-linen-50/60 via-sage-600/15 to-transparent rounded-full blur-2xl" data-id="element-159" />
          </motion.div>
        </motion.div>
      </div>
      
    </section>
    </div>;
}